import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth';
import { LandingPage } from '@/pages/Landing';
import { CanonicalTag } from '@/components/CanonicalTag';
import './index.css';
import { useEffect, Component, lazy, Suspense } from 'react';
import type { ReactNode, ErrorInfo } from 'react';
import { initSentry } from '@/lib/sentry';
import { restoreConsentFromStorage } from '@/lib/analytics';
import { captureTrackingParams } from '@/lib/trackingParams';
import { TrackingParamsPersister } from '@/components/TrackingParamsPersister';

// Restore GA4/Meta consent before any route renders, so events fired on the
// first page load (e.g. magic_link_verified on /login-success) are not dropped
// due to analytics_storage being 'denied' by default.
restoreConsentFromStorage();

// Capture gclid / utm_* params from the landing URL as early as possible so
// they are available in sessionStorage before the first route renders.
captureTrackingParams();

// Lazy-loaded layout and non-critical UI — not needed for landing page first paint
const AppLayout = lazy(() => import('@/components/layout/AppLayout').then(m => ({ default: m.AppLayout })));
const CookieBanner = lazy(() => import('@/components/CookieBanner').then(m => ({ default: m.CookieBanner })));
const LazyToaster = lazy(() => import('sonner').then(m => ({ default: m.Toaster })));

// Lazy-loaded routes — only downloaded when navigated to
const LoginPage = lazy(() => import('@/pages/Login').then(m => ({ default: m.LoginPage })));
const DashboardPage = lazy(() => import('@/pages/Dashboard').then(m => ({ default: m.DashboardPage })));
const ClientsPage = lazy(() => import('@/pages/Clients').then(m => ({ default: m.ClientsPage })));
const NewClientPage = lazy(() => import('@/pages/NewClient').then(m => ({ default: m.NewClientPage })));
const ClientDetailPage = lazy(() => import('@/pages/ClientDetail').then(m => ({ default: m.ClientDetailPage })));
const DocumentsPage = lazy(() => import('@/pages/Documents').then(m => ({ default: m.DocumentsPage })));
const UploadPortalPage = lazy(() => import('@/pages/UploadPortal').then(m => ({ default: m.UploadPortalPage })));
const ChecklistsPage = lazy(() => import('@/pages/Checklists').then(m => ({ default: m.ChecklistsPage })));
const TemplatesPage = lazy(() => import('@/pages/Templates').then(m => ({ default: m.TemplatesPage })));
const DeadlinesPage = lazy(() => import('@/pages/Deadlines').then(m => ({ default: m.DeadlinesPage })));
const SettingsPage = lazy(() => import('@/pages/Settings').then(m => ({ default: m.SettingsPage })));
const SubscriptionPage = lazy(() => import('@/pages/Subscription'));
const PrivacyPage = lazy(() => import('@/pages/Privacy').then(m => ({ default: m.PrivacyPage })));
const TermsPage = lazy(() => import('@/pages/Terms').then(m => ({ default: m.TermsPage })));
const CookiesPage = lazy(() => import('@/pages/Cookies').then(m => ({ default: m.CookiesPage })));
const AvisoLegalPage = lazy(() => import('@/pages/AvisoLegal').then(m => ({ default: m.AvisoLegalPage })));
const NotFoundPage = lazy(() => import('@/pages/NotFound').then(m => ({ default: m.NotFoundPage })));
const AccountBlockedPage = lazy(() => import('@/pages/AccountBlocked').then(m => ({ default: m.AccountBlockedPage })));
const LoginSuccessPage = lazy(() => import('@/pages/LoginSuccess').then(m => ({ default: m.LoginSuccessPage })));
const LoginErrorPage = lazy(() => import('@/pages/LoginError').then(m => ({ default: m.LoginErrorPage })));
const AuthorPage = lazy(() => import('@/pages/Author').then(m => ({ default: m.AuthorPage })));
const ComparisonPage = lazy(() => import('@/pages/Comparison').then(m => ({ default: m.ComparisonPage })));
const ChangelogPage = lazy(() => import('@/pages/Changelog').then(m => ({ default: m.ChangelogPage })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
});

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

class ToasterErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Toaster crashed:', error, info);
  }

  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

function App() {
  const { initializeAuth, isInitialized, accessToken } = useAuthStore();

  // Вызываем initializeAuth один раз при монтировании компонента
  useEffect(() => {
    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Пустой массив зависимостей - вызовется только один раз

  // Lazy-init Sentry only for authenticated users
  useEffect(() => {
    if (accessToken) {
      initSentry();
    }
  }, [accessToken]);

  // Don't render routes until auth is initialized
  if (!isInitialized) {
    return <div>Cargando...</div>;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <TrackingParamsPersister />
        <ScrollToTop />
        <CanonicalTag />
        <CookieBanner />
        <ToasterErrorBoundary>
          <LazyToaster
            richColors
            position="bottom-right"
            closeButton
            duration={4000}
            theme="light"
            visibleToasts={3}
            containerAriaLabel="Notifications"
            toastOptions={{
              closeButtonAriaLabel: 'Close notification',
            }}
          />
        </ToasterErrorBoundary>
        <Suspense fallback={<div className="flex items-center justify-center min-h-screen">Cargando...</div>}>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/login-success" element={<LoginSuccessPage />} />
            <Route path="/login-error" element={<LoginErrorPage />} />
            <Route path="/auth/verify" element={<LoginPage />} />
            <Route path="/upload/:token" element={<UploadPortalPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/cookies" element={<CookiesPage />} />
            <Route path="/aviso-legal" element={<AvisoLegalPage />} />
            <Route path="/account-blocked" element={<AccountBlockedPage />} />
            <Route path="/autor/yevhen-klymenko" element={<AuthorPage />} />
            <Route path="/alternativas" element={<ComparisonPage />} />
            <Route path="/novedades" element={<ChangelogPage />} />

            {/* Protected routes */}
            <Route element={<AppLayout />}>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/clients" element={<ClientsPage />} />
              <Route path="/clients/new" element={<NewClientPage />} />
              <Route path="/clients/:id" element={<ClientDetailPage />} />
              <Route path="/documents" element={<DocumentsPage />} />
              <Route path="/checklists" element={<ChecklistsPage />} />
              <Route path="/templates" element={<TemplatesPage />} />
              <Route path="/deadlines" element={<DeadlinesPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/subscription" element={<SubscriptionPage />} />
            </Route>
            
            {/* Fallback */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
